import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classes from './style.module.scss';
import usePageTitle from '~/app/hooks/usePageTitle';
import useTranslation from '~/app/hooks/useTranslation';
import { useAppHeader } from '~/app/modules/appHeader';
import { useHistory, useParams } from 'react-router-dom';
import { route } from '~/app/helpers/route';
import { Machine } from '~/types';
import { useStoreActions, useStoreState } from '~/store';
import useForm, { UseFormOptionFields } from '~/app/modules/form/useForm';
import useDidMountEffect from '~/app/hooks/useDidMountEffect';
import { required, requiredNonZero } from '~/app/helpers/validation';
import Loader from '~/app/common/Loader';
import { notify } from '~/app/helpers/notify';
import { toast } from 'react-toastify';
import { CreateWorkOrderFormValues, CreateWorkOrderPayload, WorkOrderExcavatorTask, WorkOrderSourceType } from '~/workorder/types';
import ProgressHeader from './ProgressHeader';
import MainForm from './MainForm';
import SecondaryForm from './SecondaryForm';
import TaskForm from './TaskForm';
import Footer, { WizardStep } from './Footer';
import moment from 'moment';
import { WORKORDER_ROUTES } from '../Router';
import { isScheduledEndBeforeStart } from './../Detail/MainContent/helper';

export type AddComponent = React.FC;

const Add: AddComponent = () => {
  const { t } = useTranslation('workorder.create');
  const { machineId } = useParams<{ machineId?: Machine['id'] }>();

  const history = useHistory();
  const { setData } = useAppHeader();
  const machine = useStoreState(state => state.machine.data);

  const [step, setStep] = useState<WizardStep>(1);
  const [selectedTasks, setSelectedTasks] = useState<WorkOrderExcavatorTask[]>([]);

  const fetching = useStoreState(state => state.workOrder.fetching);
  const createWorkOrder = useStoreActions(actions => actions.workOrder.createWorkOrder);

  const formFields = useMemo(() => {
    const fields: UseFormOptionFields = [
      ['machineId', { validators: [['required', required]] }],
      ['departmentId', { validators: [['required', requiredNonZero]] }],
      ['responsibleId', { validators: [['required', requiredNonZero]] }],
      ['plannedStartDate', { validators: [['required', required]] }],
      ['plannedEndDate', { validators: [['required', required]] }],
      'description'
    ];

    return fields;
  }, []);

  const { values, removeForm, triggerValidation } = useForm<CreateWorkOrderFormValues>({
    formName: 'createWorkOrder',
    fields: formFields,
    defaultValues: {
      machineId: machineId ?? '',
      departmentId: 0,
      responsibleId: 0,
      plannedStartDate: '',
      plannedEndDate: '',
      description: ''
    }
  });

  useDidMountEffect(() => () => {
    removeForm();
  });

  usePageTitle(t('pageTitle'));

  useEffect(() => {
    setData({
      title: t('pageTitle'),
      subTitle: machineId ? `${machine.intern} - ${machine.name}`.toUpperCase() : undefined,
      backLink: history.length > 1 ? history.goBack : route(WORKORDER_ROUTES.list),
      showUser: false,
      machineId: machineId ?? undefined,
      projectId: '',
      showMachineMessages: false,
      isIssueDetailPage: false,
      notification: false,
      search: false
    });
  }, [setData, t, history, machineId, machine]);

  const step1Valid = useMemo(() => {
    if (fetching || !values) {
      return false;
    }
    const machineOk = values.machineId !== '' && values.machineId !== undefined && values.machineId !== null;
    const departmentOk = Number(values.departmentId) > 0;
    return machineOk && departmentOk;
  }, [fetching, values]);

  const step2Valid = useMemo(() => {
    if (fetching || !values) {
      return false;
    }
    const responsibleOk = Number(values.responsibleId) > 0;
    const startOk = String(values.plannedStartDate ?? '').trim().length > 0;
    const endOk = String(values.plannedEndDate ?? '').trim().length > 0;
    return responsibleOk && startOk && endOk;
  }, [fetching, values]);

  const canSubmit = useMemo(() => {
    if (fetching || !values) {
      return false;
    }
    return true;
  }, [fetching, values]);

  const handleNext = useCallback(() => {
    if (!values) {
      return;
    }
    if (step === 1) {
      const machineErrors = triggerValidation('machineId', values.machineId);
      const departmentErrors = triggerValidation('departmentId', values.departmentId);
      if (
        machineErrors.length ||
        departmentErrors.length ||
        !step1Valid
      ) {
        toast.error(t('error.required'));
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      const responsibleErrors = triggerValidation('responsibleId', values.responsibleId);
      const startErrors = triggerValidation('plannedStartDate', values.plannedStartDate);
      const endErrors = triggerValidation('plannedEndDate', values.plannedEndDate);
      if (
        responsibleErrors.length ||
        startErrors.length ||
        endErrors.length ||
        !step2Valid
      ) {
        toast.error(t('error.required'));
        return;
      }
      const start = String(values.plannedStartDate);
      const end = String(values.plannedEndDate);
      if (isScheduledEndBeforeStart(start, end)) {
        toast.error(t('error.endBeforeStart'));
        return;
      }
      setSelectedTasks([]);
      setStep(3);
    }
  }, [values, triggerValidation, step, step1Valid, step2Valid, t]);

  const handleBack = useCallback(() => {
    setStep(current => (current === 3 ? 2 : 1));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!canSubmit) {
      toast.error(t('error.required'));
      return;
    }
    const payload: CreateWorkOrderPayload = {
      machineId: String(values.machineId),
      departmentId: Number(values.departmentId),
      responsibleId: Number(values.responsibleId),
      plannedStartDate: moment(String(values.plannedStartDate)).format('YYYY-MM-DD HH:mm:ss'),
      plannedEndDate: moment(String(values.plannedEndDate)).format('YYYY-MM-DD HH:mm:ss'),
      description: String(values.description ?? '').trim(),
      tasks: selectedTasks,
      sourceType: WorkOrderSourceType.BARDE_FELT_NORMAL,
    };
    createWorkOrder(payload).then(response => {
      if (response.success) {
        notify(t('success.created'), 'success');
        history.push(route(WORKORDER_ROUTES.detail, { id: response.workOrderId }));
      }
    });
  }, [canSubmit, t, history, values, createWorkOrder, selectedTasks]);

  if (!values) {
    return <Loader size="32" />;
  }

  const nextDisabled = step === 1 ? !step1Valid : !step2Valid;

  return (
    <div className={classes.page}>
      <div className={classes.content}>
        <ProgressHeader step={step} />
        {step === 1 && <MainForm machineId={machineId} />}
        {step === 2 && <SecondaryForm />}
        {step === 3 && (
          <TaskForm
            machineId={String(values.machineId)}
            selectedTasks={selectedTasks}
            onSelectedTasksChange={setSelectedTasks}
          />
        )}
      </div>
      <Footer
        step={step}
        onNext={handleNext}
        onBack={handleBack}
        onSubmit={handleSubmit}
        nextDisabled={nextDisabled}
        submitDisabled={!canSubmit}
      />
    </div>
  );
};

export default Add;
